import React, { useState, useEffect, useRef, Component } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIdentity } from '../contexts/IdentityContext';
import { bibleApi, bookmarksApi, notesApi } from '../lib/api';
import { ReactReader } from 'react-reader';
import {
  BookOpen, Headphones, ChevronDown, Loader2,
  Bookmark, StickyNote, Plus, X, Check, Book, Minimize2, Maximize2, Maximize, Search, AlertTriangle, ArrowDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import BIBLE_BOOKS, { getBookName } from '../data/bibleBooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Error boundary to catch ReactReader crashes (e.g. 404 EPUB)
class ReaderErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('EPUB reader error:', err?.message); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <p className="text-sm text-muted-foreground text-center">
            {this.props.fallbackText || 'Bible not available for this language'}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function BiblePage() {
  const { t, language } = useLanguage();
  const { anonId } = useIdentity();
  const [location, setLocation] = useState(() => {
    const saved = localStorage.getItem(`sg_bible_location_${language}`);
    return saved || 0;
  });
  const [showReader, setShowReader] = useState(true); // Auto-open Bible reader
  const [loading, setLoading] = useState(true); // Start with loading state
  const [mp3Tracks, setMp3Tracks] = useState([]);
  const [showAudio, setShowAudio] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [toc, setToc] = useState([]);
  
  // Book/Chapter navigation
  const [showBookSelector, setShowBookSelector] = useState(false);
  const [showChapterSelector, setShowChapterSelector] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  
  // Bookmarks & Notes
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteVerseRef, setNewNoteVerseRef] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddBookmark, setShowAddBookmark] = useState(false);
  const [newBookmarkLabel, setNewBookmarkLabel] = useState('');
  
  // Quick add bookmark/note from selection
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddPosition, setQuickAddPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [selectedCfi, setSelectedCfi] = useState('');
  
  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Mini player
  const audioRef = useRef(null);
  
  // EPUB rendition ref for direct navigation
  const renditionRef = useRef(null);
  
  // Audio player state
  const [selectedAudioBook, setSelectedAudioBook] = useState(null);
  const [selectedAudioChapter, setSelectedAudioChapter] = useState(null);
  const [audioMinimized, setAudioMinimized] = useState(false);

  // EPUB URL with browser HTTP cache (Cache-Control headers set by backend)
  const [epubAvailable, setEpubAvailable] = useState(true);
  const [epubCheckDone, setEpubCheckDone] = useState(false);
  const epubUrl = `${BACKEND_URL}/api/bible/epub/${language}/nwt.epub`;
  
  useEffect(() => {
    setEpubAvailable(true);
    setEpubCheckDone(false);
    setLoading(true);
    const checkEpub = async () => {
      try {
        const res = await bibleApi.getEpubUrl(language);
        setEpubAvailable(!!res.data?.url);
      } catch {
        setEpubAvailable(false);
      }
      setEpubCheckDone(true);
    };
    checkEpub();
  }, [language]);
  
  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Load bookmarks and notes
  useEffect(() => {
    const loadData = async () => {
      try {
        const [bkRes, ntRes] = await Promise.all([
          bookmarksApi.getBookmarks(anonId),
          notesApi.getNotes(anonId),
        ]);
        setBookmarks(bkRes.data.bookmarks || []);
        setNotes(ntRes.data.notes || []);
      } catch (err) {
        console.error('Failed to load bookmarks/notes:', err);
      }
    };
    loadData();
  }, [anonId]);

  const handleOpenBible = () => {
    setLoading(true);
    setShowReader(true);
  };

  const handleLocationChange = (epubcfi) => {
    setLocation(epubcfi);
    setLoading(false);
    localStorage.setItem(`sg_bible_location_${language}`, epubcfi);
  };

  const audioRef2 = useRef(null);
  const loadMp3Tracks = async () => {
    if (showAudio) {
      setShowAudio(false);
      return;
    }
    try {
      const res = await bibleApi.getMp3Tracks(language);
      setMp3Tracks(res.data.tracks || []);
      setShowAudio(true);
      // Scroll to audio player after a short delay
      setTimeout(() => {
        audioRef2.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      toast.info(t('scroll_down_audio') || 'Scroll down to see the audio player', { duration: 2000 });
    } catch (err) {
      toast.error(t('failed_load_audio') || 'Failed to load audio tracks');
    }
  };

  const handleAddBookmark = async () => {
    try {
      const res = await bookmarksApi.createBookmark({
        lang: language,
        epub_cfi: typeof location === 'string' ? location : null,
        label: newBookmarkLabel || `Bookmark ${bookmarks.length + 1}`,
        verse_ref: newBookmarkLabel || null,
        owner_id: anonId,
      });
      setBookmarks(prev => [res.data, ...prev]);
      setNewBookmarkLabel('');
      setShowAddBookmark(false);
      toast.success(t('bookmark_added') || 'Bookmark added!');
    } catch (err) {
      toast.error(t('failed_add_bookmark') || 'Failed to add bookmark');
    }
  };

  const handleDeleteBookmark = async (id) => {
    try {
      await bookmarksApi.deleteBookmark(id, anonId);
      setBookmarks(prev => prev.filter(b => b.id !== id));
      toast.success(t('bookmark_deleted') || 'Bookmark deleted');
    } catch (err) {
      toast.error(t('failed_delete_bookmark') || 'Failed to delete bookmark');
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    try {
      const res = await notesApi.createNote({
        lang: language,
        epub_cfi: typeof location === 'string' ? location : null,
        verse_ref: newNoteVerseRef || null,
        text: newNoteText,
        owner_id: anonId,
      });
      setNotes(prev => [res.data, ...prev]);
      setNewNoteText('');
      setNewNoteVerseRef('');
      setShowAddNote(false);
      toast.success(t('note_saved') || 'Note saved!');
    } catch (err) {
      toast.error(t('failed_save_note') || 'Failed to save note');
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await notesApi.deleteNote(id, anonId);
      setNotes(prev => prev.filter(n => n.id !== id));
      toast.success(t('note_deleted') || 'Note deleted');
    } catch (err) {
      toast.error(t('failed_delete_note') || 'Failed to delete note');
    }
  };
  
  // Handle text selection in EPUB for quick bookmark/note
  const handleTextSelection = (cfiRange, contents) => {
    const selection = contents.window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      const text = selection.toString().trim();
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      // Get position relative to viewport
      setQuickAddPosition({ 
        x: rect.left + rect.width / 2, 
        y: rect.top - 10 
      });
      setSelectedText(text);
      setSelectedCfi(cfiRange);
      setShowQuickAdd(true);
    }
  };
  
  const handleQuickAddBookmark = async () => {
    try {
      const res = await bookmarksApi.createBookmark({
        lang: language,
        epub_cfi: selectedCfi,
        label: selectedText.substring(0, 50) + (selectedText.length > 50 ? '...' : ''),
        verse_ref: selectedText.substring(0, 50),
        owner_id: anonId,
      });
      setBookmarks(prev => [res.data, ...prev]);
      setShowQuickAdd(false);
      toast.success(t('bookmark_added') || 'Bookmark added!');
    } catch (err) {
      toast.error(t('failed_add_bookmark') || 'Failed to add bookmark');
    }
  };
  
  const handleQuickAddNote = async () => {
    setNewNoteVerseRef(selectedText.substring(0, 50));
    setNewNoteText('');
    setShowQuickAdd(false);
    setShowAddNote(true);
    setShowNotes(true);
  };

  // Search function for EPUB - searches entire Bible
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error(t('search_error') || 'Please enter a search term');
      return;
    }
    
    if (!renditionRef.current || !renditionRef.current.book) {
      toast.error(t('search_error') || 'Bible not loaded yet');
      return;
    }
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const query = searchQuery.trim();
      const queryLower = query.toLowerCase();
      const book = renditionRef.current.book;
      const results = [];
      
      // Get all spine items
      const spine = book.spine;
      if (!spine || !spine.items) {
        toast.error(t('search_unavailable') || 'Unable to search');
        setIsSearching(false);
        return;
      }
      
      toast.info(t('searching') || 'Searching entire Bible...');
      
      // Search through each spine item
      for (let i = 0; i < spine.items.length && results.length < 50; i++) {
        const item = spine.items[i];
        
        try {
          // Load the section
          const doc = await book.load(item.href);
          if (!doc) continue;
          
          // Get text content
          const text = doc.body ? doc.body.textContent || '' : '';
          const textLower = text.toLowerCase();
          
          // Find all matches in this section
          let searchIndex = 0;
          let matchIndex;
          let matchCount = 0;
          
          while ((matchIndex = textLower.indexOf(queryLower, searchIndex)) !== -1 && results.length < 50 && matchCount < 3) {
            // Get excerpt with context
            const start = Math.max(0, matchIndex - 60);
            const end = Math.min(text.length, matchIndex + query.length + 60);
            let excerpt = text.substring(start, end).replace(/\s+/g, ' ').trim();
            
            // Add ellipsis if truncated
            if (start > 0) excerpt = '...' + excerpt;
            if (end < text.length) excerpt = excerpt + '...';
            
            results.push({
              id: results.length,
              href: item.href,
              excerpt: excerpt,
              query: query,
            });
            
            searchIndex = matchIndex + query.length;
            matchCount++;
          }
        } catch (err) {
          // Skip sections that fail to load
          continue;
        }
      }
      
      setSearchResults(results);
      
      if (results.length === 0) {
        toast.info(t('no_results') || 'No results found');
      } else {
        toast.success(`${results.length} ${t('results_found') || 'results found'}`);
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error(t('search_error') || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };
  
  // Navigate to search result
  const goToSearchResult = (result) => {
    if (result.href && renditionRef.current) {
      renditionRef.current.display(result.href).then(() => {
        // Try to highlight the search term after navigation
        setTimeout(() => {
          const contents = renditionRef.current.getContents();
          if (contents && contents.length > 0) {
            const win = contents[0].window;
            if (win && win.find) {
              win.find(result.query, false, false, true);
            }
          }
        }, 500);
      });
      setShowSearch(false);
    }
  };

  // Group tracks by book
  const groupedTracks = mp3Tracks.reduce((acc, track) => {
    const bookNum = track.booknum || 0;
    if (!acc[bookNum]) acc[bookNum] = [];
    acc[bookNum].push(track);
    return acc;
  }, {});

  // Flatten TOC including nested subitems
  const flattenToc = (tocItems, result = []) => {
    if (!tocItems) return result;
    for (const item of tocItems) {
      result.push(item);
      if (item.subitems && item.subitems.length > 0) {
        flattenToc(item.subitems, result);
      }
    }
    return result;
  };

  // Navigate to selected book/chapter using spine search
  const handleNavigateToChapter = async (book, chapter) => {
    setSelectedBook(book);
    setSelectedChapter(chapter);
    
    const bookName = getBookName(book, language);
    const chapterStr = chapter.toString();
    const flatToc = flattenToc(toc);
    
    // Find the book's chapter navigation page
    const bookNavItem = flatToc.find(item => {
      if (!item.label) return false;
      const labelLower = item.label.toLowerCase();
      const bookLower = bookName.toLowerCase();
      return (labelLower === bookLower || labelLower.includes(bookLower)) && 
             !labelLower.includes('outline') && !labelLower.includes('aperçu');
    });
    
    if (!bookNavItem || !bookNavItem.href) {
      toast.info(`${bookName} ${chapter} - use page arrows to navigate`);
      setShowBookSelector(false);
      setShowChapterSelector(false);
      return;
    }
    
    // Navigate to book first
    setLocation(bookNavItem.href);
    setShowBookSelector(false);
    setShowChapterSelector(false);
    
    // If chapter 1 is selected, we're done - the chapter nav page shows chapter 1 as first option
    if (chapter === 1) {
      toast.success(`${bookName}`);
      return;
    }
    
    // For other chapters, try to find and click the chapter link after a short delay
    // The EPUB's chapter navigation has clickable numbers that link to chapters
    setTimeout(() => {
      if (renditionRef.current) {
        try {
          // Try to find the chapter link in the rendered content
          const contents = renditionRef.current.getContents();
          if (contents && contents.length > 0) {
            const doc = contents[0].document;
            if (doc) {
              // Look for links with the chapter number
              // NWT EPUB chapter links often have href patterns like "#chapter{N}" or links containing the chapter number
              const links = doc.querySelectorAll('a');
              for (const link of links) {
                const text = link.textContent?.trim();
                const href = link.getAttribute('href');
                
                // Check if this link represents our target chapter
                if (text === chapterStr || 
                    text === `${chapter}` ||
                    (href && href.includes(`chapter${chapter}`) && !href.includes(`chapter${chapter}0`))) {
                  // Found the chapter link - click it
                  link.click();
                  toast.success(`${bookName} ${chapter}`);
                  return;
                }
              }
            }
          }
          // If we couldn't find/click the link, inform the user
          toast.info(`${bookName} - tap chapter ${chapter}`);
        } catch (err) {
          console.error('Error navigating to chapter:', err);
          toast.info(`${bookName} - tap chapter ${chapter}`);
        }
      } else {
        toast.info(`${bookName} - tap chapter ${chapter}`);
      }
    }, 1500); // Wait for the book's chapter nav page to render
  };

  return (
    <div className={`px-4 pt-4 pb-4 ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      {/* Header */}
      {!isFullscreen && (
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            {t('bible_reader')}
          </h1>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                isFullscreen ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground hover:bg-accent/80'
              }`}
              data-testid="bible-fullscreen-button"
            >
              <Maximize className="w-3.5 h-3.5" />
              {t('fullscreen')}
            </button>
            <button
              onClick={loadMp3Tracks}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
                showAudio ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground hover:bg-accent/80'
              }`}
              data-testid="bible-audio-button"
            >
              <Headphones className="w-3.5 h-3.5" />
              {t('audio')}
            </button>
          </div>
        </div>
      )}

      {/* Reader */}
      {showReader && (
        <div className="relative">
          {/* Toolbar */}
          <div className={`flex items-center gap-1.5 mb-3 ${isFullscreen ? 'justify-end' : ''}`}>
            {!isFullscreen && (
              <>
                <button
                  onClick={() => {
                    setShowBookSelector(!showBookSelector);
                    setShowChapterSelector(false);
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                    showBookSelector ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                  }`}
                  data-testid="bible-book-selector-button"
                >
                  <Book className="w-3.5 h-3.5" />
                  {selectedBook ? getBookName(selectedBook, language).substring(0, 12) : t('bible')}
                  {selectedChapter && ` ${selectedChapter}`}
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  onClick={() => { setShowBookmarks(!showBookmarks); setShowNotes(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                    showBookmarks ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                  }`}
                  data-testid="bible-bookmarks-button"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  {t('bookmarks')} ({bookmarks.length})
                </button>
                <button
                  onClick={() => { setShowNotes(!showNotes); setShowBookmarks(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                    showNotes ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                  }`}
                  data-testid="bible-notes-button"
                >
                  <StickyNote className="w-3.5 h-3.5" />
                  {t('notes')} ({notes.length})
                </button>
                <button
                  onClick={() => { setShowSearch(!showSearch); setShowBookmarks(false); setShowNotes(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                    showSearch ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                  }`}
                  data-testid="bible-search-button"
                >
                  <Search className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {isFullscreen && (
              <button
                onClick={() => setIsFullscreen(false)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-colors bg-primary text-primary-foreground"
                data-testid="bible-exit-fullscreen-button"
              >
                <Maximize className="w-3.5 h-3.5" />
                {t('exit_fullscreen')}
              </button>
            )}
          </div>

          {/* Book Selector */}
          <AnimatePresence>
            {showBookSelector && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 rounded-xl border border-border bg-card"
              >
                <div className="p-2 max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1">
                    {BIBLE_BOOKS.map((book) => (
                      <button
                        key={book.num}
                        onClick={() => {
                          setSelectedBook(book);
                          setSelectedChapter(1);
                          setShowBookSelector(false);
                          setShowChapterSelector(true);
                        }}
                        className={`px-2 py-1.5 rounded-lg text-xs text-left transition-colors ${
                          selectedBook?.num === book.num
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'hover:bg-accent text-foreground/80'
                        }`}
                        data-testid={`book-${book.num}`}
                      >
                        {getBookName(book, language)}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chapter Selector */}
          <AnimatePresence>
            {showChapterSelector && selectedBook && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 rounded-xl border border-border bg-card"
              >
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold">{getBookName(selectedBook, language)}</h3>
                    <button onClick={() => setShowChapterSelector(false)}>
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                  <div className="grid grid-cols-8 gap-1 max-h-40 overflow-y-auto">
                    {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((chapter) => (
                      <button
                        key={chapter}
                        onClick={() => handleNavigateToChapter(selectedBook, chapter)}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          selectedChapter === chapter
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-accent/50 hover:bg-accent text-foreground/80'
                        }`}
                        data-testid={`chapter-${chapter}`}
                      >
                        {chapter}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bookmarks panel */}
          <AnimatePresence>
            {showBookmarks && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 rounded-xl border border-border bg-card p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold">{t('bookmarks')}</h3>
                  <button onClick={() => setShowBookmarks(false)}>
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
                
                {/* Add Bookmark */}
                {showAddBookmark && (
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newBookmarkLabel}
                      onChange={(e) => setNewBookmarkLabel(e.target.value)}
                      placeholder={t("bookmark_label_placeholder") || "Bookmark label (e.g. Genesis 1:1)"}
                      className="flex-1 px-2 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--focus))]"
                      data-testid="bookmark-label-input"
                    />
                    <button
                      onClick={handleAddBookmark}
                      className="p-1.5 rounded-lg bg-primary text-primary-foreground"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bookmarks.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2 text-center">No bookmarks yet</p>
                  ) : (
                    bookmarks.map((bk) => (
                      <div key={bk.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-accent">
                        <button
                          onClick={() => { if (bk.epub_cfi) setLocation(bk.epub_cfi); }}
                          className="flex-1 text-left text-xs truncate"
                        >
                          <Bookmark className="w-3 h-3 inline mr-1.5 text-primary" />
                          {bk.label || bk.verse_ref || 'Bookmark'}
                        </button>
                        <button
                          onClick={() => handleDeleteBookmark(bk.id)}
                          className="p-1 text-muted-foreground hover:text-destructive"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notes panel */}
          <AnimatePresence>
            {showNotes && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 rounded-xl border border-border bg-card p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold">{t('notes')}</h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setShowAddNote(!showAddNote)}
                      className="p-1 rounded hover:bg-accent"
                      data-testid="bible-add-note-button"
                    >
                      <Plus className="w-3.5 h-3.5 text-primary" />
                    </button>
                    <button onClick={() => setShowNotes(false)}>
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                
                {/* Add Note */}
                {showAddNote && (
                  <div className="mb-2 space-y-2">
                    <input
                      type="text"
                      value={newNoteVerseRef}
                      onChange={(e) => setNewNoteVerseRef(e.target.value)}
                      placeholder={t("verse_ref_placeholder") || "Verse reference (optional)"}
                      className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--focus))]"
                      data-testid="note-verse-input"
                    />
                    <textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder={t("note_placeholder") || "Your note..."}
                      rows={2}
                      className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs resize-none focus:outline-none focus:ring-1 focus:ring-[hsl(var(--focus))]"
                      data-testid="note-text-input"
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={!newNoteText.trim()}
                      className="w-full py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
                      data-testid="note-save-button"
                    >
                      Save Note
                    </button>
                  </div>
                )}
                
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {notes.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2 text-center">No notes yet</p>
                  ) : (
                    notes.map((note) => (
                      <div key={note.id} className="px-2 py-1.5 rounded-lg hover:bg-accent">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {note.verse_ref && (
                              <span className="text-[10px] text-primary font-medium">{note.verse_ref}</span>
                            )}
                            <p className="text-xs text-foreground/80 line-clamp-2">{note.text}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="p-1 text-muted-foreground hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search panel */}
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 rounded-xl border border-border bg-card p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold flex items-center gap-1.5">
                    <Search className="w-3.5 h-3.5 text-primary" />
                    {t('search_bible') || 'Search Bible'}
                  </h3>
                  <button onClick={() => { setShowSearch(false); setSearchResults([]); }}>
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
                
                {/* Search Input */}
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t('search_placeholder') || 'Enter a word or phrase...'}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="bible-search-input"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !searchQuery.trim()}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="bible-search-submit"
                  >
                    {isSearching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </button>
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1 border-t border-border pt-2">
                    <p className="text-[10px] text-muted-foreground mb-1">
                      {searchResults.length} {t('results_found') || 'results found'}
                    </p>
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => goToSearchResult(result)}
                        className="w-full text-left px-2 py-2 rounded-lg hover:bg-accent text-xs transition-colors"
                        data-testid={`search-result-${result.id}`}
                      >
                        <p className="text-foreground/90 line-clamp-2">
                          ...{result.excerpt}...
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* EPUB Reader */}
          <div className="relative">
            <div className="absolute top-0 left-0 right-0 h-12 reader-glow pointer-events-none z-10 rounded-t-2xl" />
            <div
              className="rounded-2xl border border-border bg-[hsl(var(--surface))] shadow-sm overflow-hidden transition-all"
              style={{ height: isFullscreen ? 'calc(100vh - 80px)' : 'min(600px, calc(100vh - 200px))' }}
              data-testid="bible-epub-reader"
            >
              {!epubCheckDone ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">{t('loading_bible') || 'Loading Bible...'}</p>
                    <p className="text-[10px] text-muted-foreground/60">{t('loading_first_time') || 'First load may take a few seconds'}</p>
                  </div>
                </div>
              ) : !epubAvailable ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
                  <AlertTriangle className="w-10 h-10 text-amber-500" />
                  <p className="text-sm font-medium text-foreground">{t('bible_not_available') || 'Bible not available'}</p>
                  <p className="text-xs text-muted-foreground text-center">
                    {t('bible_not_available_desc') || 'The Bible is not yet available in this language on jw.org. Please select another language.'}
                  </p>
                </div>
              ) : (
              <ReaderErrorBoundary fallbackText={t('bible_not_available') || 'Bible not available for this language'}>
              <div style={{ visibility: loading ? 'hidden' : 'visible', height: '100%' }}>
              <ReactReader
                url={epubUrl}
                location={location}
                locationChanged={handleLocationChange}
                loadingView={
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                }
                tocChanged={(_toc) => {
                  setToc(_toc || []);
                }}
                getRendition={(rendition) => {
                  setLoading(false);
                  renditionRef.current = rendition;
                  
                  rendition.themes.override('font-size', 'var(--reader-font-size)');
                  rendition.themes.override('font-family', 'var(--font-heading)');
                  rendition.themes.override('line-height', '1.7');
                  
                  // ULTRA-AGGRESSIVE CSS fixes for EPUB layout - works for ALL Bible books
                  // Force EVERYTHING to static positioning with NO transforms
                  rendition.themes.override('*', 'position: static !important; transform: none !important; left: auto !important; right: auto !important; top: auto !important; bottom: auto !important; float: none !important; margin: 0 !important; box-sizing: border-box !important');
                  
                  // Force proper table structure with spacing
                  rendition.themes.override('table', 'border-collapse: separate !important; border-spacing: 2px !important; width: 100% !important; table-layout: fixed !important; display: table !important');
                  rendition.themes.override('tbody', 'display: table-row-group !important');
                  rendition.themes.override('tr', 'display: table-row !important; height: auto !important; min-height: 60px !important');
                  rendition.themes.override('td', 'display: table-cell !important; text-align: center !important; vertical-align: middle !important; padding: 18px 12px !important; min-height: 60px !important; min-width: 80px !important; position: static !important; white-space: normal !important');
                  rendition.themes.override('th', 'display: table-cell !important; text-align: center !important; vertical-align: middle !important; padding: 18px 12px !important; min-height: 60px !important; position: static !important');
                  
                  // Force links to fill cells completely
                  rendition.themes.override('a', 'display: block !important; width: 100% !important; height: 100% !important; padding: 12px 8px !important; text-decoration: none !important; color: inherit !important; position: static !important');
                  rendition.themes.override('span', 'display: inline !important; position: static !important');
                  rendition.themes.override('.entry', 'display: block !important; width: 100% !important; position: static !important');
                  
                  // Add selection handler for bookmarks/notes
                  rendition.on('selected', handleTextSelection);
                  
                  // Enable swipe navigation for mobile
                  let touchStartX = 0;
                  let touchStartY = 0;
                  rendition.hooks.content.register((contents) => {
                    const doc = contents.document;
                    const body = doc.body;
                    
                    // Swipe handler
                    doc.addEventListener('touchstart', (e) => {
                      touchStartX = e.changedTouches[0].screenX;
                      touchStartY = e.changedTouches[0].screenY;
                    }, { passive: true });
                    
                    doc.addEventListener('touchend', (e) => {
                      const dx = e.changedTouches[0].screenX - touchStartX;
                      const dy = e.changedTouches[0].screenY - touchStartY;
                      // Only trigger if horizontal swipe > 50px and more horizontal than vertical
                      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                        if (dx > 0) {
                          rendition.prev(); // Swipe right → previous page
                        } else {
                          rendition.next(); // Swipe left → next page
                        }
                      }
                    }, { passive: true });
                    
                    // Improve touch behavior for text selection
                    if (body) {
                      body.style.userSelect = 'text';
                      body.style.webkitUserSelect = 'text';
                      body.style.overscrollBehavior = 'contain';
                    }
                    
                    // Add CSS to improve text selection on mobile
                    const style = doc.createElement('style');
                    style.textContent = `
                      * {
                        -webkit-touch-callout: default !important;
                        -webkit-user-select: text !important;
                        user-select: text !important;
                      }
                      body {
                        touch-action: pan-x pan-y !important;
                        overscroll-behavior: contain !important;
                      }
                      ::selection {
                        background: rgba(0, 128, 128, 0.3) !important;
                      }
                    `;
                    doc.head.appendChild(style);
                  });
                }}
              />
              </div>
              </ReaderErrorBoundary>
              )}
            </div>
          </div>
          
          {/* Quick Add Bookmark/Note Popup */}
          <AnimatePresence>
            {showQuickAdd && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed z-50 bg-card border border-border rounded-xl shadow-lg p-2"
                style={{
                  left: `${quickAddPosition.x}px`,
                  top: `${quickAddPosition.y}px`,
                  transform: 'translate(-50%, -100%)',
                }}
              >
                <div className="flex gap-1">
                  <button
                    onClick={handleQuickAddBookmark}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    data-testid="quick-add-bookmark"
                  >
                    <Bookmark className="w-3.5 h-3.5" />
                    {t('add_bookmark') || 'Bookmark'}
                  </button>
                  <button
                    onClick={handleQuickAddNote}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    data-testid="quick-add-note"
                  >
                    <StickyNote className="w-3.5 h-3.5" />
                    {t('add_note') || 'Note'}
                  </button>
                  <button
                    onClick={() => setShowQuickAdd(false)}
                    className="p-2 rounded-lg text-xs hover:bg-accent transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Audio Player Panel */}
      <AnimatePresence>
        {showAudio && (
          <motion.div
            ref={audioRef2}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mt-4 rounded-2xl border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Headphones className="w-4 h-4 text-primary" />
                {t('audio_bible') || 'Audio Bible'}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAudioMinimized(!audioMinimized)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="audio-minimize-button"
                >
                  {audioMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setShowAudio(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {currentTrack && (
              <div className="mb-4 p-3 rounded-xl bg-accent/30">
                <p className="text-xs font-medium text-foreground mb-2 truncate">
                  {decodeHtmlEntities(currentTrack.title)}
                </p>
                <audio
                  ref={audioRef}
                  controls
                  src={currentTrack.url}
                  className="w-full h-8"
                  data-testid="audio-mini-player"
                />
              </div>
            )}

            {!audioMinimized && (
              <>
                {/* Book Selector for Audio */}
                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                    {t('select_book') || 'Select Book'}
                  </label>
                  <select
                    value={selectedAudioBook?.num || ''}
                    onChange={(e) => {
                      const book = BIBLE_BOOKS.find(b => b.num === parseInt(e.target.value));
                      setSelectedAudioBook(book);
                      setSelectedAudioChapter(null);
                      setCurrentTrack(null);
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="audio-book-select"
                  >
                    <option value="">{t('choose_book') || 'Choose a book...'}</option>
                    {BIBLE_BOOKS.filter(book => (groupedTracks[book.num] || []).length > 0).map(book => {
                      const bookTracks = groupedTracks[book.num] || [];
                      return (
                        <option key={book.num} value={book.num}>
                          {getBookName(book, language)} ({bookTracks.length})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Chapter Selector for Audio */}
                {selectedAudioBook && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                      {t('select_chapter') || 'Select Chapter'}
                    </label>
                    <select
                      value={selectedAudioChapter?.title || ''}
                      onChange={(e) => {
                        const track = (groupedTracks[selectedAudioBook.num] || []).find(
                          t => t.title === e.target.value
                        );
                        setSelectedAudioChapter(track);
                        setCurrentTrack(track);
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="audio-chapter-select"
                    >
                      <option value="">{t('choose_chapter') || 'Choose a chapter...'}</option>
                      {(groupedTracks[selectedAudioBook.num] || []).map((track, i) => (
                        <option key={i} value={track.title}>
                          {decodeHtmlEntities(track.title)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quick stats */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">
                    {Object.keys(groupedTracks).length} {t('books_available') || 'books'} — {mp3Tracks.length} {t('audio_chapters') || 'audio chapters'}
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
